"use client"

import Link from "next/link"
import { useState, useEffect } from "react"

type B2BRegistrationNoteProps = {
  className?: string
  onClick?: () => void
  variant?: "default" | "compact" | "hero"
}

const B2BRegistrationNote = ({ className = "", onClick, variant = "default" }: B2BRegistrationNoteProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    // Detect if device supports touch
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  const handleButtonClick = (e: React.MouseEvent) => {
    if (isTouchDevice && (variant === "compact" || variant === "hero")) {
      e.preventDefault()
      setIsTooltipOpen(!isTooltipOpen)
    } else if (onClick) {
      onClick()
    }
  }

  const handleTooltipClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  useEffect(() => {
    // Close tooltip when clicking outside on mobile
    if (isTooltipOpen && isTouchDevice && (variant === "compact" || variant === "hero")) {
      const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        const target = e.target as HTMLElement
        if (!target.closest('.b2b-tooltip-container')) {
          setIsTooltipOpen(false)
        }
      }
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('touchend', handleClickOutside)
      return () => {
        document.removeEventListener('click', handleClickOutside)
        document.removeEventListener('touchend', handleClickOutside)
      }
    }
  }, [isTooltipOpen, isTouchDevice, variant])

  if (variant === "compact") {
    return (
      <div className="relative group/b2b b2b-tooltip-container">
        <Link
          href="https://horeca.krek.si"
          className={`text-sm text-neutral-600 hover:text-navy underline decoration-neutral-300 hover:decoration-navy transition-colors duration-200 ${className}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="b2b-register-link"
          onClick={handleButtonClick}
        >
          Za podjetja
        </Link>
        {/* Tooltip on hover (desktop) or click (mobile) */}
        <div 
          className={`absolute right-0 top-full mt-1 w-64 p-3.5 bg-white border border-neutral-200 rounded-lg shadow-lg transition-all duration-200 z-50 ${
            isTouchDevice 
              ? (isTooltipOpen ? 'opacity-100 visible' : 'opacity-0 invisible')
              : 'opacity-0 invisible group-hover/b2b:opacity-100 group-hover/b2b:visible hover:opacity-100 hover:visible'
          }`}
          onClick={handleTooltipClick}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-neutral-600"
              >
                <path
                  d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6V10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 14H10.01"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-xs leading-relaxed text-left flex-1 text-neutral-700">
              V kolikor nakupujete v imenu podjetja vam priporočamo registracijo na{" "}
              <a 
                href="https://horeca.krek.si" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-navy underline decoration-navy/50 hover:decoration-navy"
              >
                www.horeca.krek.si
              </a>, kjer imate možnost povpraševanja in širšo ponudbo izdelkov.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (variant === "hero") {
    return (
      <div className="relative group/b2b w-full z-[100] b2b-tooltip-container">
        <Link
          href="https://horeca.krek.si"
          className={`inline-flex items-center justify-center w-full text-white/80 hover:text-white border border-white/40 rounded-md py-2 text-sm font-normal transition-all duration-200 ${className}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="b2b-register-link"
          onClick={handleButtonClick}
        >
          Za podjetja
        </Link>
        {/* Tooltip on hover (desktop) or click (mobile) */}
        <div 
          className={`absolute right-0 bottom-full mb-1 w-64 p-3.5 bg-white border border-neutral-200 rounded-lg shadow-lg transition-all duration-200 z-[9999] ${
            isTouchDevice 
              ? (isTooltipOpen ? 'opacity-100 visible' : 'opacity-0 invisible')
              : 'opacity-0 invisible group-hover/b2b:opacity-100 group-hover/b2b:visible hover:opacity-100 hover:visible'
          }`}
          onClick={handleTooltipClick}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-neutral-600"
              >
                <path
                  d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6V10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 14H10.01"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-xs leading-relaxed text-left flex-1 text-neutral-700">
              V kolikor nakupujete v imenu podjetja vam priporočamo registracijo na{" "}
              <a 
                href="https://horeca.krek.si" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-navy underline decoration-navy/50 hover:decoration-navy"
              >
                www.horeca.krek.si
              </a>, kjer imate možnost povpraševanja in širšo ponudbo izdelkov.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Link
      href="https://horeca.krek.si"
      className={`w-full p-3.5 bg-neutral-50 rounded-tl-none rounded-bl-none rounded-tr-lg rounded-br-lg hover:bg-neutral-100 transition-all duration-200 group ${className}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="b2b-register-link"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-neutral-600"
          >
            <path
              d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 6V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 14H10.01"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-xs sm:text-sm leading-relaxed text-left flex-1 text-neutral-700 group-hover:text-neutral-900">
          V kolikor nakupujete v imenu podjetja vam priporočamo registracijo na{" "}
          <span className="font-semibold text-navy underline decoration-navy/50 hover:decoration-navy">www.horeca.krek.si</span>, kjer imate možnost povpraševanja in širšo ponudbo izdelkov.
        </p>
      </div>
    </Link>
  )
}

export default B2BRegistrationNote
